import { Fragment, type ReactNode } from 'react'
import { cx } from '../../cx'

/**
 * A deliberately small markdown renderer: headings, paragraphs, lists, fenced
 * code, GFM tables, inline code, bold, italic and links. Output is React
 * elements, never innerHTML, so nothing in the text can become markup.
 * Anything it does not understand renders as the text it was.
 *
 * Promoted from product-synthai (it imports nothing but React) so Datafabric's
 * connector setup guides can use it. `repairModelText` turns on the
 * seam-splitting heuristics written for MODEL output ("…question. ## The
 * Evidence …1. What…"); authored docs leave it off, because the same
 * heuristics would tear a sentence-case heading in half.
 *
 * Links are OPT-IN (`links`), for authored text only: then http(s) targets
 * become anchors, opened in a new tab with rel="noopener noreferrer", and any
 * other scheme renders as its label text. Off (the default), `[label](url)`
 * stays literal text — model output must not turn into a clickable link.
 */

const SAFE_HREF = /^https?:\/\//i
const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)/g
const INLINE_WITH_LINKS = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)\s]+\))/g

/**
 * Plain text, optionally one span per word (`wordClass`) so a caller can
 * animate words as they arrive. Index keys keep earlier words mounted while
 * the text grows; whitespace stays a bare text node.
 */
function words(text: string, key: string, wordClass?: string): ReactNode {
  if (!wordClass) return text
  return (
    <Fragment key={key}>
      {text.split(/(\s+)/).map((w, j) => (!w || /^\s+$/.test(w) ? w : <span key={j} className={wordClass}>{w}</span>))}
    </Fragment>
  )
}

function inline(text: string, key: number, links: boolean, wordClass?: string): ReactNode {
  const parts: ReactNode[] = []
  const re = new RegExp(links ? INLINE_WITH_LINKS : INLINE)
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(words(text.slice(last, m.index), `${key}-t${i}`, wordClass))
    const tok = m[0]
    const k = `${key}-${i++}`
    if (tok.startsWith('`')) parts.push(<code key={k}>{tok.slice(1, -1)}</code>)
    else if (tok.startsWith('**')) parts.push(<strong key={k}>{words(tok.slice(2, -2), `${k}-s`, wordClass)}</strong>)
    else if (tok.startsWith('[')) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!
      const label = link[1]!
      const href = link[2]!
      parts.push(
        SAFE_HREF.test(href) ? (
          <a key={k} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
        ) : (
          <Fragment key={k}>{label}</Fragment>
        ),
      )
    } else parts.push(<em key={k}>{words(tok.slice(1, -1), `${k}-e`, wordClass)}</em>)
    last = m.index + tok.length
  }
  if (last < text.length) parts.push(words(text.slice(last), `${key}-t${i}`, wordClass))
  // Always the same keyed wrapper, even around one part: a line that gains
  // its first token mid-reveal must not change its child's key, or every
  // word already on screen remounts and fades in again.
  return <Fragment key={key}>{parts}</Fragment>
}

/** A list or heading marker standing alone as a word. */
const BLOCK_MARKER = /^(?:#{1,6}|\d+\.|[-*•])$/
/** A line's own leading block marker — not half of an inline token. */
const LINE_LEAD = /^\s*(?:#{1,6}\s+|\d+\.\s|[-*•]\s)/

/**
 * Where a word-by-word reveal of `text` may cut, at or after `i`: the end of
 * the word `i` lands in, run on past any inline token the cut would split
 * (code, bold, italic, and links when `links`) and past a bare list/heading
 * marker. A prefix cut here renders as a prefix of the final rendering — no
 * literal `**The` that flips to bold a tick later, no `1.` paragraph that
 * turns into a list. Tokens are matched per line with the same patterns
 * `Markdown` renders with, so the two cannot drift. A marker with no closer
 * renders literally either way, so nothing is waited for.
 */
export function markdownRevealEnd(text: string, i: number, opts?: { links?: boolean }): number {
  const wordEnd = (j: number) => {
    while (j < text.length && !/\s/.test(text[j]!)) j++
    return j
  }
  let j = wordEnd(Math.max(0, Math.min(text.length, i)))
  for (;;) {
    let k = j
    const lineStart = text.lastIndexOf('\n', k - 1) + 1
    const nl = text.indexOf('\n', lineStart)
    const line = text.slice(lineStart, nl === -1 ? text.length : nl)
    const re = new RegExp(opts?.links ? INLINE_WITH_LINKS : INLINE)
    re.lastIndex = LINE_LEAD.exec(line)?.[0].length ?? 0
    let m: RegExpExecArray | null
    while ((m = re.exec(line))) {
      const start = lineStart + m.index
      const end = start + m[0].length
      if (start >= k) break
      if (k < end) {
        k = wordEnd(end)
        break
      }
    }
    if (k === j && j < text.length) {
      const word = /\S+$/.exec(text.slice(0, j))?.[0]
      if (word && BLOCK_MARKER.test(word)) {
        let n = j
        while (n < text.length && /\s/.test(text[n]!)) n++
        k = wordEnd(n)
      }
    }
    if (k === j) return j
    j = k
  }
}

type Block =
  | { kind: 'h'; level: number; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'code'; text: string }
  | { kind: 'table'; head: string[]; rows: string[][] }

const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l)
const isTableSep = (l: string) => /^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/.test(l)
const cells = (l: string) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())

/**
 * Models often emit headings and list items glued onto one line
 * ("…question. ## The Evidence …1. What…"). Split those seams before
 * parsing so the structure survives.
 */
function normalise(src: string): string {
  return src
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => {
      // The splitters below recover structure the model glued into a
      // SENTENCE, so they must not see a line's own leading heading marker:
      // over "## 1. The answer" they tore the number off and left a literal
      // "##" above an ordered list (seen live 2026-09-22). Hold that marker
      // aside, split what follows, put it back — glued structure LATER in
      // the same heading line still splits.
      const lead = /^\s*#{1,6}\s+/.exec(line)?.[0] ?? ''
      const rest = line.slice(lead.length)
      return (
        lead +
        rest
          .replace(/\s+(#{1,6}\s)/g, '\n\n$1')
          .replace(/([^\n])\s+(\d+\.\s)/g, '$1\n$2')
          .replace(/([^\n])\s+([-*•]\s)/g, '$1\n$2')
      )
    })
    .join('\n')
}

const STOPWORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'is', 'of', 'on', 'or', 'the', 'to', 'vs', 'with', 'not', 'does', 'do'])

/**
 * A glued heading runs straight into its first sentence: "The Answer We
 * cannot provide…". Headings are title case; the sentence is not. The
 * heading ends before the first lowercase word that is not a stopword, and
 * the capitalised word just before it ("We") started the sentence, so it
 * goes with the paragraph.
 */
function splitGluedHeading(text: string): [string, string] {
  const words = text.split(/\s+/)
  let cut = -1
  for (let w = 1; w < words.length; w++) {
    const word = words[w]!
    if (/^[a-z]/.test(word) && !STOPWORDS.has(word.toLowerCase().replace(/[^a-z]/g, ''))) {
      cut = w
      break
    }
  }
  // The capitalised word before the cut is the sentence's first word, and its
  // presence is the whole evidence that a sentence is glued on here. Without
  // requiring it, an ordinary sentence-case heading gets torn in half:
  // "## The answer, first" cuts at "answer," and renders as a heading "The"
  // above a paragraph "answer, first" (seen live 2026-08-28).
  if (cut < 2 || !/^[A-Z]/.test(words[cut - 1]!)) return [text, '']
  const end = cut - 1
  const head = words.slice(0, end).join(' ').replace(/[.:!?]$/, '')
  const rest = words.slice(end).join(' ')
  return head ? [head, rest] : [text, '']
}

function parse(src: string, repair: boolean): Block[] {
  const lines = (repair ? normalise(src) : src.replace(/\r\n/g, '\n')).split('\n')
  const blocks: Block[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]!
    if (!line.trim()) {
      i++
      continue
    }
    if (line.startsWith('```')) {
      const buf: string[] = []
      i++
      while (i < lines.length && !lines[i]!.startsWith('```')) buf.push(lines[i++]!)
      i++
      blocks.push({ kind: 'code', text: buf.join('\n') })
      continue
    }
    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1]!)) {
      const head = cells(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && isTableRow(lines[i]!)) rows.push(cells(lines[i++]!))
      blocks.push({ kind: 'table', head, rows })
      continue
    }
    // A bare marker with nothing after it is not a heading and must not fall
    // through to the paragraph branch, which printed it as literal "##".
    if (/^\s*#{1,6}\s*$/.test(line)) {
      i++
      continue
    }
    const h = /^\s*(#{1,6})\s+(.*)$/.exec(line)
    if (h) {
      const raw = h[2]!.trim()
      // "## 1. The answer" — the number belongs to the heading, and a
      // numbered heading is never a glued sentence.
      const [text, rest] = !repair || /^\d+[.)]\s/.test(raw) ? [raw, ''] : splitGluedHeading(raw)
      blocks.push({ kind: 'h', level: h[1]!.length, text })
      if (rest) blocks.push({ kind: 'p', text: rest })
      i++
      continue
    }
    if (/^\s*\d+\.\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*\d+\.\s/, ''))
      blocks.push({ kind: 'ol', items })
      continue
    }
    if (/^\s*[-*•]\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*[-*•]\s/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*[-*•]\s/, ''))
      blocks.push({ kind: 'ul', items })
      continue
    }
    // Always consume the current line first: a line that LOOKS like a table
    // row (matches the stop-regex's `\s*\|` clause) but has no separator
    // beneath it falls through from the table branch above into here, and
    // the same stop-regex would match it again — never advancing `i` and
    // looping forever. Only lines AFTER this one are subject to the stop
    // rules.
    const buf: string[] = [lines[i++]!]
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,6}\s|```|\s*\d+\.\s|\s*[-*•]\s|\s*\|)/.test(lines[i]!)) buf.push(lines[i++]!)
    blocks.push({ kind: 'p', text: buf.join(' ') })
  }
  return blocks
}

export interface MarkdownProps {
  text: string
  className?: string
  /** Repair structure a MODEL glued onto one line. Off for authored docs. */
  repairModelText?: boolean
  /** Render http(s) `[label](url)` as anchors. Authored docs only; off for model output. */
  links?: boolean
  /** Wrap each word of prose (not code) in a span with this class — for word-by-word reveal animations. */
  wordClass?: string
}

export function Markdown({ text, className, repairModelText = false, links = false, wordClass }: MarkdownProps) {
  const blocks = parse(text, repairModelText)
  const line = (s: string, key: number) => inline(s, key, links, wordClass)
  return (
    <div className={cx('bui-md', className)}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'h': {
            const Tag = (`h${Math.min(6, b.level + 2)}` as unknown) as 'h3'
            return <Tag key={i}>{line(b.text, i)}</Tag>
          }
          case 'p':
            return <p key={i}>{line(b.text, i)}</p>
          case 'ul':
            return <ul key={i}>{b.items.map((it, j) => <li key={j}>{line(it, i * 100 + j)}</li>)}</ul>
          case 'ol':
            return <ol key={i}>{b.items.map((it, j) => <li key={j}>{line(it, i * 100 + j)}</li>)}</ol>
          case 'code':
            return <pre key={i}><code>{b.text}</code></pre>
          case 'table':
            return (
              <div key={i} className="bui-md-table">
                <table>
                  <thead><tr>{b.head.map((c, j) => <th key={j} scope="col">{line(c, i * 100 + j)}</th>)}</tr></thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j}>{r.map((c, k) => <td key={k}>{line(c, i * 10_000 + j * 100 + k)}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}

export const __test = { parse, normalise }
